import {
  Button,
  Center,
  HStack,
  Image,
  Stack,
  Text,
  useOutsideClick,
  useTheme,
  VStack,
} from "@chakra-ui/react";
import React, { useRef, useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { Props } from "../types";
import { Loading } from "semente-js";
import { fadeInUp } from "src/animations";
import { Company } from "src/types";
import { MenuButton } from "src/components/MenuButton";
import { Modal } from "src/components/base/Modal";
import { ProjectContext } from "src/contexts/ProjectContext";
import Icon from "src/components/base/Icon";
import { createImageFromInitials } from "src/common";
import ProjectsService from "src/services/projects";
import { useNavigate } from "react-router-dom";

const CompaniesTabProject: React.FC<Props> = ({ project }) => {
  const [menuIsOpen, setMenuIsOpen] = useState<string>();
  const { showProjectDialog, handleRemoveCompany } = useContext(ProjectContext);
  const [companies, setCompanies] = useState({
    data: [] as Company[],
    isLoading: true,
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState<{
    isLoading: boolean;
    id: string;
  }>();
  const navigate = useNavigate();
  const menuRef = useRef<any>(null);
  const theme = useTheme();

  useOutsideClick({
    ref: menuRef,
    handler: () => setMenuIsOpen(undefined),
  });

  useEffect(() => {
    setCompanies({ isLoading: true, data: [] });
    ProjectsService.getCompaniesByProject(project.id).then((data) =>
      setCompanies({ isLoading: false, data })
    );
  }, [project]);

  const menuOptions = [
    {
      title: "Ver empresa",
      color: "#141414",
      onClick: (company_id: Company["id"]) => {
        navigate(`companies/${company_id}`);
      },
    },
    {
      title: "Remover empresa",
      color: "#CC0000",
      onClick: (company_id: Company["id"]) => {
        setShowConfirmationModal({
          isLoading: false,
          id: company_id,
        });
        setMenuIsOpen(undefined);
      },
    },
  ];

  const handleDeleteCompany = async () => {
    if (showConfirmationModal) {
      setShowConfirmationModal({ ...showConfirmationModal, isLoading: true });
      await handleRemoveCompany(showConfirmationModal.id);
      setCompanies({
        ...companies,
        data: companies.data.filter(
          (company) => company.id !== showConfirmationModal.id
        ),
      });
      setShowConfirmationModal(undefined);
    }
  };

  return (
    <VStack flex={1} alignItems={"flex-start"}>
      <VStack w={"100%"} maxW={"558px"} spacing={0}>
        <HStack w={"100%"} justifyContent={"space-between"}>
          <Text
            fontSize={"RH_lg"}
            fontFamily={"raleway"}
            color={"Gray.$800"}
            fontWeight={500}
          >
            Empresas
          </Text>

          {!companies.isLoading && companies.data.length >= 1 && (
            <Button
              minH={"36px"}
              onClick={() => showProjectDialog("companies")}
            >
              <HStack spacing={2}>
                <Icon name={"plus"} />

                <Text fontSize={"L_sm"} color={"Gray.$800"} fontWeight={500}>
                  Adicionar empresas
                </Text>
              </HStack>
            </Button>
          )}
        </HStack>

        {companies.isLoading ? (
          <Center height={"300px"} w={"100%"}>
            <Loading
              height="100"
              width="100"
              color="#408EC5"
              secondaryColor="#408EC5"
              radius="12.5"
              ariaLabel="mutating-dots-loading"
              visible={true}
            />
          </Center>
        ) : (
          <motion.div {...fadeInUp} style={{ width: "100%" }}>
            {companies.data.length >= 1 ? (
              <VStack
                border={"1px solid"}
                borderColor={"Gray.$300"}
                mt={"32px !important"}
                borderRadius={"16px"}
                pl={"16px"}
                w={"100%"}
              >
                {companies.data.map((company, index) => {
                  return (
                    <HStack
                      cursor={"inherit"}
                      key={company?.id}
                      alignItems={"flex-start"}
                      m={"0px !important"}
                      pt={"16px"}
                      w={"100%"}
                    >
                      <Center
                        w={"48px"}
                        h={"48px"}
                        borderRadius={"50%"}
                        background={"Tertiary.pure"}
                      >
                        <Image
                          src={
                            company?.image_url ||
                            createImageFromInitials({
                              background: (theme.colors as any).Tertiary.pure,
                              color: (theme.colors as any).white,
                              name: company?.name.substring(0, 2) || "",
                              size: 48,
                              fontSize: 16,
                            })
                          }
                          borderRadius={"50%"}
                          objectFit={"cover"}
                          w={"100%"}
                          h={"100%"}
                        />
                      </Center>
                      <HStack
                        ml={"12px !important"}
                        flex={1}
                        position={"relative"}
                        pb={"26px"}
                        pr={"16px"}
                        borderBottom={"1px solid"}
                        borderColor={
                          companies.data.length - 1 === index
                            ? "transparent"
                            : "Gray.$300"
                        }
                      >
                        <HStack
                          mt={"12px !important"}
                          justifyContent={"space-between"}
                          w={"100%"}
                        >
                          <Text
                            fontWeight={company ? 400 : 600}
                            fontSize={"L_md"}
                            color={"Gray.$800"}
                          >
                            {company?.name}
                          </Text>

                          <Center
                            cursor={"pointer"}
                            onClick={() => setMenuIsOpen(company.id)}
                          >
                            <MenuButton />
                          </Center>

                          {company.id === menuIsOpen && (
                            <VStack
                              py={"8px"}
                              ref={menuRef}
                              right={"0px"}
                              top={"40px"}
                              position={"absolute"}
                              zIndex={200}
                              background={"Gray.$100"}
                              minW={"240px"}
                              borderRadius={"12px"}
                              boxShadow={"0px 2px 16px rgba(0, 0, 0, 0.12)"}
                            >
                              {menuOptions.map((option) => (
                                <Stack
                                  cursor={"pointer"}
                                  key={option.title}
                                  padding={"12px 16px"}
                                  onClick={() => option.onClick(company.id)}
                                  w={"100%"}
                                  alignItems={"flex-start"}
                                  transition={"300ms all"}
                                  _hover={{
                                    background: "Gray.$200",
                                  }}
                                >
                                  <Text
                                    fontSize={"L_sm"}
                                    fontWeight={400}
                                    color={option.color}
                                  >
                                    {option.title}
                                  </Text>
                                </Stack>
                              ))}
                            </VStack>
                          )}
                        </HStack>
                      </HStack>
                    </HStack>
                  );
                })}
              </VStack>
            ) : (
              <VStack w={"558px"} mt={"36px"}>
                <Icon
                  name={"question-circle"}
                  color={"#0077CC"}
                  size={"64px"}
                />
                <Text
                  fontSize={"RH_xs"}
                  color={"Gray.$800"}
                  fontFamily={"raleway"}
                  fontWeight={500}
                >
                  Nenhuma empresa aqui ainda
                </Text>
                <Text fontSize={"L_md"} color={"Gray.$700"} fontWeight={400}>
                  Convide empresas para esse projeto.
                </Text>

                <Button
                  minH={"36px"}
                  mt={"16px !important"}
                  onClick={() => showProjectDialog("companies")}
                >
                  <HStack spacing={2}>
                    <Icon name={"plus"} />

                    <Text
                      fontSize={"L_sm"}
                      color={"Gray.$800"}
                      fontWeight={500}
                    >
                      Adicionar empresa
                    </Text>
                  </HStack>
                </Button>
              </VStack>
            )}
          </motion.div>
        )}
      </VStack>

      <Modal
        onClose={() => setShowConfirmationModal(undefined)}
        isOpen={!!showConfirmationModal}
        title={"Remover empresa"}
      >
        {showConfirmationModal?.isLoading ? (
          <Center
            maxW={"568px"}
            minH={"300px"}
            borderTop={"1px solid"}
            borderColor={"Gray.$400"}
          >
            <VStack maxW={"400px"}>
              <Loading
                height="100"
                width="100"
                color="#408EC5"
                secondaryColor="#408EC5"
                radius="12.5"
                ariaLabel="mutating-dots-loading"
                visible={true}
              />

              <Text
                mt={"24px !important"}
                fontSize={"L_md"}
                fontFamily={"lato"}
                color={"Gray.$800"}
                fontWeight={400}
                textAlign={"center"}
              >
                Aguarde, estamos removendo essa empresa e seus quadros
                subjacentes.
              </Text>
            </VStack>
          </Center>
        ) : (
          <VStack
            maxW={"568px"}
            borderTop={"1px solid"}
            borderColor={"Gray.$400"}
          >
            <Text
              p={"24px"}
              fontSize={"L_md"}
              fontFamily={"lato"}
              color={"Gray.$800"}
              fontWeight={400}
            >
              Tem certeza que deseja remover essa empresa do projeto? A empresa
              será removida do quadro de consultorias e todo o histórico será
              perdido.
            </Text>

            <VStack
              px={"24px"}
              py={"24px"}
              spacing={"8px !important"}
              w={"100%"}
              borderTop={"1px solid"}
              borderColor={"Gray.$400"}
            >
              <Button
                w={"100%"}
                variant={"Primary"}
                onClick={handleDeleteCompany}
              >
                Remover empresa
              </Button>
              <Button
                w={"100%"}
                variant={"Secondary"}
                onClick={() => setShowConfirmationModal(undefined)}
              >
                Voltar
              </Button>
            </VStack>
          </VStack>
        )}
      </Modal>
    </VStack>
  );
};

export default CompaniesTabProject;
